/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import CloudDetailsCard from "./CloudCard";
import { mapCloudDetailToSections } from "kontrolEdge/constants/constants";
import { fetchCloudConnectionAPI, fetchCloudErrorsAPI, fetchCloudProvidersAPI, fetchCloudSubscriptionAPI } from "../requests/request";
import { useAppSelector } from "@redux/hooks";
import NotificationsView from "kontrolEdge/components/Shared/Notifications/NotificationsView";
import { RootState } from "@redux/store/store";
import BandwidthChartUniversal from "kontrolEdge/components/Shared/Bandwidth/BandwidthChartUniversal";

  export interface LatencyDetail {
    destination: string;
    timestamp: string;
    unit: string;
    value: number
  }
  export interface SubscriptionDetail {
    subscriptionId: string;
    contractMonths: string;
    autoRenew: string;
    renewDate: string;
    status: string;
    id: string;
  }

  export interface ProviderDetail {
    cloudRegion: string;
    cloudVLAN: string;
    sourceVLAN: string;
    state: string;
    connectionCloudId: string;
  }

  export interface CloudConnectivityDetail {
    id: string;                  
    renewDate: string;
    name: string;
    provider?: string;
    automaticVlan: string;
    lag?: {
      subid: string;
    }        
    customer?: { name: string };
    contractMonths: string;      
    subscriptionId: string;      
    cloud: string;     
    account: string;          
    cloudLocation: string;       
    connectionName: string;      
    mbps: string;                
    parentConnection: string;   
    parentConnectionId: string;
    tag: string;                
    vlan: string;  
    performanceMetrics?: {
      errors: number;
      latency?: LatencyDetail
    };
    subscription?: SubscriptionDetail;
    providerInfo?: ProviderDetail;
    port: {
      id: number;
      name: string;
      portType?: "physical" | "virtual";
      datacenter: {
        datacenterId: string;
        name: string;
        displayName: string;
        direction: string;
        gps: string;
        capacityKW: string;
        phase: string;
        cage: string;
        rackId: string;
        rackCapacity: string;
        patchPanelODF: string;
        rackSecurityPin: string;
        status: string;
        visible: boolean;
        providerCode: string;
        city: string;
        providerId: number;
        createdAt: string;
        updatedAt: string;
      };
    };            
  }

type LocationState = { connectionDetail?: CloudConnectivityDetail, tenantOverride?: number; };

const CloudConnectivityDetails: React.FC = () => {
    const location = useLocation();
    const reduxTenantId = useAppSelector((state) => state.selection.tenantId);

    const { connectionDetail: preloaded, tenantOverride } =
    (location.state as LocationState) || {};
    const [detail, setDetail] = useState<CloudConnectivityDetail | undefined>(
      preloaded
    );
    // Decide which one to actually send to every API call:
    const effectiveTenantId = tenantOverride ?? reduxTenantId;
    const [searchParams] = useSearchParams();
    const [isFetching, setIsFetching] = useState(false);
    const overlay = useProcessingOverlay(isFetching);
    const api = useAxiosAuth0();
    const userProfile = useAppSelector((state: RootState) => state.userProfile);
    const { canAccessAlerts } = userProfile;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const connectionId = searchParams.get("connectionId")!;
    const sections = useMemo(
      () => mapCloudDetailToSections(detail),
        [detail]
    );

    const portType =
    // we might carry in location.state
    (location.state as any)?.portType
    // try to read from detail.port
    ?? (detail as any)?.port?.portType
    // default unknown -> CloudBandwidthChart will treat as virtual (30m)
    ?? undefined;


        useEffect(() => {
          //Re-run whenever connectionId or refresh changes
          if (!connectionId) return;
          let cancelled = false;
          setIsFetching(true);
        
          (async () => {
            try {
              //Core detail – show it immediately
              const coreResp = await fetchCloudConnectionAPI(api, connectionId, effectiveTenantId);
              const fresh: CloudConnectivityDetail = coreResp.data.data;
              if (cancelled) return;
              setDetail(fresh);
        
              //Extras in parallel
              const [
                errorsSettled,
                subscriptionSettled,
                providersSettled
              ] = await Promise.allSettled([
                fetchCloudErrorsAPI(api, connectionId, effectiveTenantId),
                fetchCloudSubscriptionAPI(api, connectionId,effectiveTenantId),
                fetchCloudProvidersAPI(api, connectionId,effectiveTenantId)
              ]);
        
              //Merge successful results
              if (errorsSettled.status === 'fulfilled') {
                fresh.performanceMetrics = {
                  ...fresh.performanceMetrics,
                  errors: errorsSettled.value.data.data.crcErrors
                };
              } else {
                console.warn('CRC errors failed:', errorsSettled.reason);
              }
              if (subscriptionSettled.status === 'fulfilled') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (fresh as any).subscription = subscriptionSettled.value.data.data;
              } else {
                console.warn('Subscription fetch failed:', subscriptionSettled.reason);
              }
              if (providersSettled.status === 'fulfilled') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (fresh as any).providerInfo = providersSettled.value.data.data;
              } else {
                console.warn('Providers fetch failed:', providersSettled.reason);
              }
              
        
              if (cancelled) return;
              //Push the merged object back into state
              setDetail({ ...fresh });
        
            } catch (err) {
              console.error('Error loading InternetDetails:', err);
            } finally {
              if (!cancelled) setIsFetching(false);
            }
          })();
        
          return () => { cancelled = true; };
        }, [api, connectionId]);    

    if (isFetching || !detail) return <>{overlay}</>;

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
                {sections.map((section, idx) => (
                    <CloudDetailsCard key={idx} data={section} connectionDetail={detail} tenantId={effectiveTenantId}/>
                ))}
            </div>
            {/* Cloud Utilization chart */}
            <BandwidthChartUniversal
                service="cloud"
                id={connectionId}
                tenantId={effectiveTenantId}
                portType={portType}
            />
            {/* {canAccessAlerts && connectionId && (
              <NotificationsView serviceType="cloud" serviceId={connectionId}/>
            )} */}
        </>
    );
};

export default CloudConnectivityDetails;
